import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Pause, Play, RotateCcw, Square } from 'lucide-react';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { FOCUS_LENGTHS, MIN_LOGGED_MINUTES, formatClock, minutesFocused } from './timer';
import { useLogFocus, useStudyStreak } from './useStudyStreak';

type TimerState =
  | { status: 'idle' }
  | { status: 'running'; endsAt: number }
  | { status: 'paused'; msLeft: number };

/** The last 7 days as dots, today last; a filled dot is a day with a test or 10+ focus minutes. */
function WeekDots({ days }: { days: Array<{ date: string; active: boolean }> }) {
  const { i18n } = useTranslation();
  const week = days.slice(-7);
  return (
    <ol className="flex gap-1.5" aria-hidden>
      {week.map((day, index) => (
        <li key={day.date} className="flex flex-col items-center gap-1">
          <span
            className={cn(
              'h-6 w-6 rounded-full border',
              day.active ? 'border-orange-500 bg-orange-500' : 'border-border bg-muted',
              index === week.length - 1 && 'ring-2 ring-orange-500/30 ring-offset-1 ring-offset-card',
            )}
          />
          <span className="text-[10.5px] uppercase text-muted-foreground">
            {new Date(`${day.date}T12:00:00Z`).toLocaleDateString(i18n.language, { weekday: 'narrow', timeZone: 'UTC' })}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** The study streak and a focus timer whose sessions count toward it. */
export function StudyStreakCard() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data } = useStudyStreak();
  const logFocus = useLogFocus();
  const [length, setLength] = useState<number>(25);
  const [timer, setTimer] = useState<TimerState>({ status: 'idle' });
  const [now, setNow] = useState(() => Date.now());
  const loggedRef = useRef(false);

  const msLeft = timer.status === 'running' ? timer.endsAt - now : timer.status === 'paused' ? timer.msLeft : length * 60_000;

  // A clock from the end time, not a counter: a throttled background tab still finishes on time.
  useEffect(() => {
    if (timer.status !== 'running') return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [timer.status]);

  const finish = (minutes: number) => {
    setTimer({ status: 'idle' });
    if (minutes < MIN_LOGGED_MINUTES || loggedRef.current) return;
    loggedRef.current = true;
    logFocus.mutate(minutes, {
      onSuccess: () => toast({ title: t('streak.focus_logged', { count: minutes }), variant: 'success' }),
      onError: () => toast({ title: t('streak.focus_failed'), variant: 'destructive' }),
    });
  };

  const timeUp = timer.status === 'running' && msLeft <= 0;
  useEffect(() => {
    if (timeUp) finish(length);
    // Runs once when the time is up; finish and length are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeUp]);

  const start = () => {
    loggedRef.current = false;
    const left = timer.status === 'paused' ? timer.msLeft : length * 60_000;
    setNow(Date.now());
    setTimer({ status: 'running', endsAt: Date.now() + left });
  };

  const current = data?.current ?? 0;

  return (
    <section aria-labelledby="study-streak-title" className="rounded-lg border border-border bg-card p-5 md:p-6">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-full', current ? 'bg-orange-100 text-orange-600 dark:bg-orange-950/50 dark:text-orange-400' : 'bg-muted text-muted-foreground')}>
            <Flame className="h-6 w-6" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id="study-streak-title" className="text-[15px] font-semibold text-foreground">
              {current ? t('streak.current', { count: current }) : t('streak.start')}
            </h2>
            <p className="mt-0.5 text-[13px] text-muted-foreground">
              {data?.today.tests || (data?.today.focusMinutes ?? 0) >= 10
                ? t('streak.today_done')
                : t('streak.today_todo')}
              {data && data.best > current ? ` · ${t('streak.best', { count: data.best })}` : ''}
            </p>
          </div>
        </div>
        {data && <WeekDots days={data.days} />}
      </div>

      <div className="mt-5 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="font-mono text-2xl font-semibold tabular-nums text-foreground" role="timer" aria-live="off">
            {formatClock(msLeft)}
          </span>
          {timer.status === 'idle' && (
            <div role="radiogroup" aria-label={t('streak.length')} className="flex gap-1">
              {FOCUS_LENGTHS.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  role="radio"
                  aria-checked={length === minutes}
                  onClick={() => setLength(minutes)}
                  className={cn(
                    'h-7 rounded-md px-2 text-[12.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    length === minutes ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  {t('streak.minutes_short', { count: minutes })}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {timer.status === 'running' ? (
            <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setTimer({ status: 'paused', msLeft })}>
              <Pause className="h-4 w-4" aria-hidden /> {t('streak.pause')}
            </Button>
          ) : (
            <Button type="button" size="sm" className="gap-1.5" onClick={start}>
              <Play className="h-4 w-4" aria-hidden /> {timer.status === 'paused' ? t('streak.resume') : t('streak.focus')}
            </Button>
          )}
          {timer.status !== 'idle' && (
            <>
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => finish(minutesFocused(length, msLeft))}>
                <Square className="h-4 w-4" aria-hidden /> {t('streak.stop')}
              </Button>
              <Button type="button" variant="ghost" size="sm" aria-label={t('streak.reset')} onClick={() => setTimer({ status: 'idle' })}>
                <RotateCcw className="h-4 w-4" aria-hidden />
              </Button>
            </>
          )}
        </div>
      </div>
      {(data?.today.focusMinutes ?? 0) > 0 && (
        <p className="mt-2 text-[12.5px] text-muted-foreground">{t('streak.today_focus', { count: data!.today.focusMinutes })}</p>
      )}
    </section>
  );
}

export default StudyStreakCard;
